import { createContext, useContext, useReducer } from 'react'
import { DataMatchSearchMode, DataMatchSelection, Stepper } from '../types'

type DataMatchPageStateMachine = {
  firstRowHeader: 0 | 1
  inputRange: string
  outputRange: string[]
  primaryColumnOptions: string[]
  primaryIndices: number[]
  stepper: Stepper
  inputRangeFocus: boolean
  tableData: DataMatchSelection[]
  searchMode: DataMatchSearchMode
  selectedFolders: string[]
  duplicatedColumns: string[]
  outputTableError: boolean
}

const initialState: DataMatchPageStateMachine = {
  firstRowHeader: 0,
  inputRange: '',
  outputRange: [],
  primaryColumnOptions: [],
  primaryIndices: [],
  stepper: Stepper.INPUT_SELECTION,
  inputRangeFocus: true,
  tableData: [],
  searchMode: DataMatchSearchMode.NONE,
  selectedFolders: [],
  duplicatedColumns: [],
  outputTableError: false,
}

type DataMatchPageStateMachineAction =
  | {
      type: 'SET_FIRST_ROW_HEADER'
      payload: 0 | 1
    }
  | {
      type: 'SET_INPUT_RANGE'
      payload: string
    }
  | {
      type: 'SET_OUTPUT_RANGE'
      payload: string[]
    }
  | {
      type: 'SET_PRIMARY_COLUMN_OPTIONS'
      payload: string[]
    }
  | {
      type: 'SET_PRIMARY_INDICES'
      payload: number[]
    }
  | {
      type: 'SET_STEP'
      payload: Stepper
    }
  | {
      type: 'SET_INPUT_RANGE_FOCUS'
      payload: boolean
    }
  | { type: 'SET_TABLE_DATA'; payload: DataMatchSelection[] }
  | {
      type: 'SET_SEARCH_MODE'
      payload: DataMatchSearchMode
    }
  | {
      type: 'SET_SELECTED_FOLDERS'
      payload: string[]
    }
  | {
      type: 'SET_DUPLICATED_COLUMNS'
      payload: string[]
    }
  | {
      type: 'SET_OUTPUT_TABLE_ERROR'
      payload: boolean
    }
  | {
      type: 'BATCH'
      payload: Partial<DataMatchPageStateMachine>
    }

const reducer = (
  state: DataMatchPageStateMachine,
  action: DataMatchPageStateMachineAction
): DataMatchPageStateMachine => {
  switch (action.type) {
    case 'BATCH': {
      return { ...state, ...action.payload }
    }
    case 'SET_OUTPUT_TABLE_ERROR': {
      return { ...state, outputTableError: action.payload }
    }
    case 'SET_DUPLICATED_COLUMNS': {
      return { ...state, duplicatedColumns: action.payload }
    }
    case 'SET_SELECTED_FOLDERS': {
      return { ...state, selectedFolders: action.payload }
    }
    case 'SET_SEARCH_MODE': {
      return { ...state, searchMode: action.payload }
    }
    case 'SET_TABLE_DATA': {
      return { ...state, tableData: action.payload }
    }
    case 'SET_INPUT_RANGE_FOCUS': {
      return { ...state, inputRangeFocus: action.payload }
    }
    case 'SET_STEP': {
      return { ...state, stepper: action.payload }
    }
    case 'SET_PRIMARY_INDICES': {
      return { ...state, primaryIndices: action.payload }
    }
    case 'SET_PRIMARY_COLUMN_OPTIONS':
      return { ...state, primaryColumnOptions: action.payload }
    case 'SET_OUTPUT_RANGE':
      return { ...state, outputRange: action.payload }
    case 'SET_INPUT_RANGE':
      return { ...state, inputRange: action.payload }
    case 'SET_FIRST_ROW_HEADER':
      return { ...state, firstRowHeader: action.payload }
    default:
      return state
  }
}

const DataMatchPageStateContext = createContext<{
  state: DataMatchPageStateMachine
  dispatch: React.Dispatch<DataMatchPageStateMachineAction>
}>(null as any)

// HOF that returns a component wrapped with context
const withDataMatchPageStateContext = <P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> => {
  const WrappedComponent = (props: P) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    const contextValue = { state, dispatch }

    return (
      <DataMatchPageStateContext.Provider value={contextValue}>
        <Component {...props} />
      </DataMatchPageStateContext.Provider>
    )
  }

  return WrappedComponent
}

// Hook to use the context
const useDataMatchPageState = () => {
  const context = useContext(DataMatchPageStateContext)
  if (!context) {
    throw new Error(
      'useDataMatchPageState must be used within a DataMatchPageStateContext Provider'
    )
  }
  return context
}

export {
  DataMatchPageStateContext,
  withDataMatchPageStateContext,
  useDataMatchPageState,
  type DataMatchPageStateMachine,
  type DataMatchPageStateMachineAction,
}
